import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "priceshow"
}
const _hoisted_2 = { class: "priceshow__container" }
const _hoisted_3 = { class: "container-fluid" }
const _hoisted_4 = { class: "priceshow__results" }
const _hoisted_5 = { class: "priceshow__products" }
const _hoisted_6 = {
  key: 0,
  class: "col-12 fs-regular"
}
const _hoisted_7 = {
  key: 0,
  class: "priceshow__products no-filter"
}
const _hoisted_8 = { class: "col-search" }
const _hoisted_9 = { class: "col-search__sticky" }

import {
  reactive,
  onMounted,
  computed,
  watch,
  ref,
  onBeforeUnmount, nextTick,
} from 'vue';
import {
  useStore,
} from 'vuex';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import BaseEqualizer from '@/components/BaseEqualizer.vue';
import ProductCard from '@/components/product/PriceCard.vue';
import { Product } from '@/classes';
import { DataLayer } from '@/classes/DataLayer';
import PriceSearchCard from '@/components/PriceSearchCard.vue';
import Headline from '@/components/Headline.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'PriceshowPage',
  setup(__props) {

const route = useRoute();
const store = useStore();
const router = useRouter();
const stickyCont = ref();
const priceshowEqualizer = ref<InstanceType<typeof BaseEqualizer>>();

const routeQueryVars = computed(() => route.query);
const routeParamVars = computed(() => route.params);

const isFetching = computed(() => store.getters['priceshow/loading']);
const products = computed(() => store.getters['priceshow/productsFiltered']);

const cartItemKey = ref(routeQueryVars.value.cik);

const priceshow = reactive({
  params: {
    productId: routeQueryVars.value.pid,
    double: routeParamVars.value.usageHt ? 1 : 0,
    usage: parseInt(`${routeParamVars.value.usage}`, 10),
    usageHt: parseInt(`${routeParamVars.value.usageHt || 0}`, 10),
    postcode: routeParamVars.value.postcode,
    city: routeQueryVars.value.stadt,
    street: routeQueryVars.value.strasse,
    houseNumber: routeQueryVars.value.hausnummer,
    branch: routeParamVars.value.branch,
    business: parseInt(`${routeQueryVars.value?.gewerbe || 0}`, 10),
    oeko: parseInt(`${routeQueryVars.value?.oeko || 0}`, 10),
    affiliateId: routeQueryVars.value?.aid,
  },
});

const productsSorted = ref<Product[]>([]);
const productsNotFiltered = ref<Product[]>([]);

const pushAvailableProductsToLayer = () => {
  DataLayer.push({ event: null });
  if (products.value.length <= 0) {
    DataLayer.push({
      event: 'no_tariff_available',
      customer_type: priceshow.params.business ? 'company' : 'private',
      postcode: priceshow.params.postcode,
      consumption: priceshow.params.usage,
      energy_type: priceshow.params.branch,
    });
  } else {
    DataLayer.push({
      event: 'available_tariffs',
      customer_type: priceshow.params.business ? 'company' : 'private',
      postcode: priceshow.params.postcode,
      consumption: priceshow.params.usage,
      offers: productsSorted.value.map((product: Product) => product.attributes.name),
      energy_type: priceshow.params.branch,
    });
  }
};

const pushCalculateToLayer = () => {
  DataLayer.push({ event: null });
  if (products.value.length <= 0) {
    DataLayer.push({
      event: 'calculate_error',
      customer_type: priceshow.params.business ? 'company' : 'private',
      message: 'Leider konnten für Ihre Angaben aktuell keine Preise ermittelt werden.',
      postcode: priceshow.params.postcode,
      consumption: priceshow.params.usage,
      energy_type: priceshow.params.branch,
    });
  } else {
    DataLayer.push({
      event: 'calculate',
      customer_type: priceshow.params.business ? 'company' : 'private',
      postcode: priceshow.params.postcode,
      consumption: priceshow.params.usage,
      energy_type: priceshow.params.branch,
    });
  }
};

watch(() => products.value, () => {
  const pid: string = Array.isArray(routeQueryVars.value.pid)
    ? routeQueryVars.value.pid.join(',')
    : routeQueryVars.value.pid ?? '';

  if (pid) {
    const matchingProducts = [...products.value].filter(($pa) => pid.split(',').map(Number).includes($pa.id));
    const nonMatchingProducts = [...products.value].filter(($pa) => !pid.split(',').map(Number).includes($pa.id));

    productsSorted.value = matchingProducts;
    productsNotFiltered.value = nonMatchingProducts;
  } else {
    productsSorted.value = ([...products.value] as Product[]).sort(($pa, $pb) => {
      if (
        ($pa.isRecommended && !routeQueryVars.value.pid)
          || pid.split(',').map(Number).includes($pa.id)
      ) {
        return -1;
      }

      return $pa.getPriceValue('yearly') > $pb.getPriceValue('yearly') ? 1 : -1;
    });
    productsNotFiltered.value = [];
  }

  const filterProductsByOeko = (productsFilter: any[], o: boolean) => productsFilter.filter((p) => p.attributes.details.contractDetails.isEco === o);

  if (routeQueryVars.value.oeko === '1') {
    productsSorted.value = filterProductsByOeko(productsSorted.value, true);
  }

  pushCalculateToLayer();
  pushAvailableProductsToLayer();

  nextTick(() => {
    if (priceshowEqualizer.value) {
      priceshowEqualizer.value.runEqualize();
    }
  });
});

const fetchPrices = () => {
  store.dispatch('priceshow/fetch', priceshow.params);
};

const setPriceshowParams = () => {
  priceshow.params.city = routeQueryVars.value.stadt;
  priceshow.params.street = routeQueryVars.value.strasse;
  priceshow.params.houseNumber = routeQueryVars.value.hausnummer;
  priceshow.params.branch = routeParamVars.value.branch;
  priceshow.params.postcode = routeParamVars.value.postcode;
  priceshow.params.double = routeParamVars.value.usageHt ? 1 : 0;
  priceshow.params.usage = parseInt(`${routeParamVars.value.usage}`, 10);
  priceshow.params.usageHt = parseInt(`${routeParamVars.value.usageHt || 0}`, 10);
  priceshow.params.business = parseInt(`${routeQueryVars.value?.gewerbe || 0}`, 10);
  priceshow.params.oeko = parseInt(`${routeQueryVars.value.oeko || 0}`, 10);
  priceshow.params.productId = routeQueryVars.value.pid;
};

const showOtherBranchModalProduct = ref<Product | null>(null);

const headlineContent = computed(() => (productsSorted.value.length === 0
  ? `Kein passender ${priceshow.params.branch === 'strom' ? 'Strom' : 'Gas'} - Tarif gefunden`
  : 'Ihr persönliches Angebot.'));

watch(() => ({
  ...routeParamVars.value,
  ...routeQueryVars.value,
}), () => {
  showOtherBranchModalProduct.value = null;
  setPriceshowParams();
  fetchPrices();
}, { deep: true });

onMounted(() => {
  fetchPrices();
});

const clickOrder = ($product: Product) => {
  store.commit('priceshow/SET_LOADING', true);

  store.dispatch('cart/add', {
    product: $product,
    cartItemKey: cartItemKey.value,
    params: priceshow.params,
    entry: true,
  }).then((response) => {
    if (response.cartEnabled) {
      router.push('/warenkorb/');
    } else {
      router.push('/bestellung/adresse/');
    }
  });
};

const setStickyTop = () => {
  const header = document.querySelector('header');
  if (stickyCont.value && header) {
    stickyCont.value.style.top = `${header.offsetHeight}px`;
  }
};

onMounted(() => {
  window.addEventListener('resize', setStickyTop);
  window.addEventListener('load', setStickyTop);
  setStickyTop();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setStickyTop);
  window.removeEventListener('load', setStickyTop);
});

return (_ctx: any,_cache: any) => {
  const _component_BasePageHeader = _resolveComponent("BasePageHeader")!
  const _component_BaseEqualizerElement = _resolveComponent("BaseEqualizerElement")!
  const _component_BaseLoader = _resolveComponent("BaseLoader")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!isFetching.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
          _createVNode(_component_BasePageHeader, null, {
            title: _withCtx(() => [
              _createTextVNode(_toDisplayString(headlineContent.value), 1)
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(BaseEqualizer, {
                    ref_key: "priceshowEqualizer",
                    ref: priceshowEqualizer
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(productsSorted.value, (product, inx) => {
                        return (_openBlock(), _createBlock(_component_BaseEqualizerElement, {
                          class: "priceshow__products-card",
                          key: `product-${product.id}`
                        }, {
                          default: _withCtx(() => [
                            _createVNode(ProductCard, {
                              product: product,
                              recommended: product.isRecommended,
                              position: inx + 1,
                              "is-single": productsSorted.value.length === 1,
                              onCart: clickOrder
                            }, null, 8, ["product", "recommended", "position", "is-single"])
                          ]),
                          _: 2
                        }, 1024))
                      }), 128)),
                      (productsSorted.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Auf Basis Ihrer Angaben zu PLZ bzw. Verbrauch können wir Ihnen leider kein Angebot unterbreiten "))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 512),
                  (productsNotFiltered.value.length >= 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        _createVNode(Headline, {
                          "text-align": "left",
                          size: "h1",
                          "tag-name": "h2"
                        }, {
                          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                            _createTextVNode(" Weitere Produkte. ", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(BaseEqualizer, {
                          ref_key: "priceshowEqualizer",
                          ref: priceshowEqualizer
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(productsNotFiltered.value, (product, inx) => {
                              return (_openBlock(), _createBlock(_component_BaseEqualizerElement, {
                                class: "priceshow__products-card",
                                key: `product-${product.id}`
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(ProductCard, {
                                    product: product,
                                    recommended: product.isRecommended,
                                    position: inx + 1,
                                    "is-single": productsNotFiltered.value.length === 1,
                                    onCart: clickOrder
                                  }, null, 8, ["product", "recommended", "position", "is-single"])
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _: 1
                        }, 512)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", {
                    ref_key: "stickyCont",
                    ref: stickyCont
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      _createVNode(PriceSearchCard, {
                        options: priceshow.params
                      }, null, 8, ["options"])
                    ])
                  ], 512)
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (isFetching.value)
      ? (_openBlock(), _createBlock(_component_BaseLoader, { key: 1 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})